/**
 * "OZORA 2026 - The Dome" / "Shpongle · starts 22:00 (in 10 min)" (plan.md step 60;
 * the wording, the user, 2026-10-15).
 *
 * In the phone app a reminder is a LOCAL notification, scheduled on the phone
 * from the stored bundle: it fires with no network. On the web the server
 * sends it as push. Both use these words and these ids, so a set that moves is
 * one reminder rescheduled, never two.
 */
/**
 * How long before a saved set a person is reminded — SEVERAL times if they
 * like, each any whole number of minutes (the user, 2026-10-07: "let the user
 * add multiple triggers, with a custom number; the default for all users is
 * 10 min"). Stored as `notificationPrefs.reminderMinutes`: a list, `[]` for
 * none chosen on purpose; absent is the default.
 */
export const DEFAULT_REMINDER_LEADS: readonly number[] = [10];
export const MAX_REMINDER_LEAD = 240;
export const MAX_REMINDERS = 5;

export function isReminderLead(n: unknown): n is number {
  return typeof n === 'number' && Number.isInteger(n) && n >= 1 && n <= MAX_REMINDER_LEAD;
}

/**
 * A stored value as the leads it means, longest first, no repeats: a list as
 * itself (bad entries dropped, at most `MAX_REMINDERS`); an older single
 * number as one; `null` or `[]` as none; nothing stored as the default.
 */
export function readReminderLeads(v: unknown): number[] {
  if (v === undefined) return [...DEFAULT_REMINDER_LEADS];
  const list = Array.isArray(v) ? v : v === null ? [] : [v];
  return [...new Set(list.filter(isReminderLead))].sort((a, b) => b - a).slice(0, MAX_REMINDERS);
}

/** One reminder per act, START TIME and lead — a moved set has new ids. */
export function reminderId(actId: string, startAt: Date, lead?: number): string {
  return lead === undefined ? `${actId}@${startAt.toISOString()}` : `${actId}@${startAt.toISOString()}#${lead}`;
}

/** "7 min", "1 h", "1 h 30 min" — how far ahead a reminder is. */
export function leadText(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}

/**
 * The words of a set reminder (the user, 2026-10-15; three lines since 2026-11-02): the title
 * "MyRave.io | <rave edition>", the artist, then "starts in 10 min at <stage>" (a push cannot tick;
 * the always-on card is the one that counts down). Never "moved": a set that moves is told ONCE, when its time
 * changes (the schedule's notice, `held.ts`) — the user, 2026-11-04.
 */
export interface ReminderTextInput {
  rave: string; act: string; stage: string | null; startAt: Date; timezone: string; minutes: number;
}

export function reminderText(r: ReminderTextInput): { title: string; body: string } {
  const lead = leadText(r.minutes);
  const when = `starts in ${lead}${r.stage ? ` at ${r.stage}` : ''}`;
  return { title: reminderTitle(r.rave), body: [r.act, when].join('\n') };
}

/**
 * THREE lines, all an iPhone's preview shows (the user, 2026-11-02 — four were cut): "MyRave.io | <rave>", the
 * artist, "starts in 10 min at <stage>". Every builder (the phone's, the server's) takes this title.
 */
export function reminderTitle(rave: string): string {
  return rave ? `MyRave.io | ${rave}` : 'MyRave.io';
}

export interface PlannedReminder {
  id: string; actId: string; at: string; title: string; body: string;
  /** Where a tap goes — a set reminder: the rave's Live with the set popped out (`#soon=<slug>`, 2026-10-31). */
  url?: string;
}

/** The saved acts' reminders still ahead of `now`, soonest first. */
export function planReminders(
  acts: readonly { id: string; name: string; startAt: string | Date; stageName?: string | null }[],
  saved: ReadonlySet<string>, leads: number | readonly number[], now: Date,
  rave: { name: string; timezone: string },
  /**
   * The words — English (`reminderText`) unless given: the phone's page passes
   * its own, in the reader's language (the user, 2026-10-27).
   */
  text: (r: ReminderTextInput) => { title: string; body: string } = reminderText,
): PlannedReminder[] {
  const list = readReminderLeads(typeof leads === 'number' ? [leads] : leads);
  const out: PlannedReminder[] = [];
  for (const a of acts) {
    if (!saved.has(a.id)) continue;
    const start = new Date(a.startAt);
    for (const minutes of list) {
      const at = new Date(start.getTime() - minutes * 60_000);
      if (at.getTime() <= now.getTime()) continue;
      out.push({ id: reminderId(a.id, start, minutes), actId: a.id, at: at.toISOString(), ...text({ rave: rave.name, act: a.name, stage: a.stageName ?? null, startAt: start, timezone: rave.timezone, minutes }) });
    }
  }
  return out.sort((x, y) => x.at.localeCompare(y.at));
}
